import BaseFallbackHandler from "../base_fallback_handler";
import {SYSTEM_EVENTS} from "../../../../events/events";
import Event from "../../../../utils/event";
import {TimeSpan} from "@sparkware/app-utils";
import _ from "../../../../utils/underscore";
import { IFreePlayVoucherData } from "../../../../../../common/interfaces/free-play/free-play-voucher-data.interface";


const REFRESH_WALLET_INTERVAL = 30; // in seconds

export default class WalletFallbackHandler extends BaseFallbackHandler {

    static CLASS = 'WalletFallbackHandler';
    static FallbackName = 'Wallet';
    private _subscription;
    private _eventInstances = [];
	_uri = '';
	_refreshInterval = 0;

    init(params) {
        super.init(params);
        if (params) {
             this._uri = params.uri || '/capi/wallet/getplayerwallet/';
             this._refreshInterval = params.refreshInterval || REFRESH_WALLET_INTERVAL;
        }
    }

    start() {
        if (true === this._isActive) {
            this.logger.debug('Fallback is already running');
            return;
        }
       this._startWalletRefresh();
    }


    getFallbackName() {
        return 'Wallet';
    }


    /**
     * Update wallet over API
     * @private
     */
    private _receiveWallet() {
        if (this.serviceFactory.oAuth2Service().isAuthorized()) {
            this.serviceFactory.http2Service().post(this._uri)
                .then((walletResponse) => {
                    // reduce data to update separate system parts (wallet and free play services)
                    const walletData = Object.assign({}, walletResponse);
                    delete walletData['FreePlay'];
                    this._handleWalletData(walletData);
                    this._handleFreePlay(walletResponse.FreePlay);
                });
        }
    }

    private _handleWalletData(walletData) {
        this.logger.debug('Received Wallet data', JSON.stringify(walletData));
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_WALLET, {
            Balance: walletData.AvailableMoney,
            Currency: walletData.CurrencyCode,
            RealMoneyBankroll: walletData.RealMoneyBankroll
        });
    }

	private _convertWalletFreePlays(fpData: any): IFreePlayVoucherData[] {
        if (!fpData || !fpData.FreePlayList || !fpData.FreePlayList.FreePlay) {
            this.logger.debug('Unable to convert wallet FP data to FP Message format. FreePlay data is empty');
            return [];
        }
        this.logger.debug('Received Free Play data', JSON.stringify(fpData));
        let vouchers: IFreePlayVoucherData[] = [];
        if (_.isArray(fpData.FreePlayList.FreePlay)) {
			vouchers = fpData.FreePlayList.FreePlay.map(freePlay => this._convertToVoucher(freePlay));
		} else if (_.isObject(fpData.FreePlayList.FreePlay)) {
			vouchers = [this._convertToVoucher(fpData.FreePlayList.FreePlay)];
		}
        return vouchers;
	}

	private _convertToVoucher(freePlay: any): IFreePlayVoucherData {
		const voucher: IFreePlayVoucherData = {
			ID: freePlay.Id.toString(),
			CurrentBalance: _.safeStringToNumber(freePlay.CurrAmount, 0),
			Winnings: _.safeStringToNumber(freePlay.FreeBetWinning, 0),
			FreePlayTypeId: freePlay.FreePlayTypeID ? freePlay.FreePlayTypeID.toString() : '1',
			OriginAmount: _.safeStringToNumber(freePlay.OrigAmount, 0),
			ExpirationDate: new Date(freePlay.FreePlayExpirationDate),
			SpinBetAmount: _.safeStringToNumber(freePlay.MaxBetAmount, 0),
			GameTypeIDList: freePlay.GameTypeList ? this._getGameTypesFromWalletFreePlay(freePlay.GameTypeList) : []
		};
		return voucher;
	}


	private _getGameTypesFromWalletFreePlay(gameTypeList: any): number[] {
        const gameTypes: number[] = [];
        if (_.isArray(gameTypeList.GameType)) {
            gameTypeList.GameType.forEach((game) => {
                gameTypes.push(Number(game["@id"]));
            });
        } else {
            gameTypes.push(Number(gameTypeList.GameType["@id"]));
        }
        return gameTypes;
    }

    private _lastReceivedVouchers: IFreePlayVoucherData[] = null;
    private _handleFreePlay(fpData: any) {
		const newReceivedVouchers = this._convertWalletFreePlays(fpData);
		if(this._lastReceivedVouchers) {
		    const removedVouchers = this._extractRemovedVouchers(newReceivedVouchers);
		    if(removedVouchers.length > 0) {
		        removedVouchers.forEach( voucher => {
                    Event.fire(SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_REMOVED, voucher.ID);
                })
            }
        }
        this.logger.debug('Received FreePlay voucers', JSON.stringify(newReceivedVouchers));
        Event.fire(SYSTEM_EVENTS.SESSION_EVENT_FREE_PLAY_RECEIVED, newReceivedVouchers);
        this._lastReceivedVouchers = newReceivedVouchers;
    }

    private _extractRemovedVouchers(newVouchers:IFreePlayVoucherData[]): IFreePlayVoucherData[] {
        return this._lastReceivedVouchers.filter(oldVoucher => newVouchers.findIndex( newVoucher => newVoucher.ID === oldVoucher.ID) === -1)
    }

    /**
     * Refresh wallet.
     */
    private _startWalletRefresh() {
        if (false === this.serviceFactory.oAuth2Service().isAuthorized()) {
            this.stop();
            return;
        }
        this._isActive = true;
        // Get wallet and FP data from token and local storage
        this._receiveWallet();

        this._subscription = this.serviceFactory.observableFactoryService().fromInterval(TimeSpan.fromSeconds(this._refreshInterval)).forEach(() => {
            this._receiveWallet();
        });

        this._eventInstances.push(Event.on([SYSTEM_EVENTS.OAUTH_STATE_CHANGE, SYSTEM_EVENTS.CLOSE_GAME, SYSTEM_EVENTS.ROUTE_CHANGED], () => {
            this._receiveWallet();
        }, this));
    }

    stop() {
        // clear interval
        if (this._subscription) {
            this._subscription.dispose();
        }
        // unsubscribe from events
        Event.detachEvents(this._eventInstances);
        this._isActive = false;
    }
}